import { Controller } from "@hotwired/stimulus"

// Infinite scroll for the My Tasks page list (same approach as the disabled-scheduling report).
// Each page arrives as bare rows; group headings an earlier page already rendered are dropped.
// The next page is requested from the number of rows on screen (not a page number), so rows
// removed by ticking/deleting don't cause a skip; rows already shown are dropped on append.
export default class extends Controller {
  static targets = ["list", "loading", "trigger"];
  static values = { url: String, perPage: Number };

  connect() {
    this.isLoading = false;
    this.hasMore = this.hasTriggerTarget;
    if (!this.hasMore) return;

    this.observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting) && this.hasMore && !this.isLoading) {
        this.loadNextPage();
      }
    }, { rootMargin: "200px" });
    this.observer.observe(this.triggerTarget);
  }

  disconnect() {
    if (this.observer) this.observer.disconnect();
  }

  async loadNextPage() {
    this.isLoading = true;
    if (this.hasLoadingTarget) this.loadingTarget.style.display = "block";

    try {
      const offset = this.listTarget.querySelectorAll(".SchoolTask").length;
      const response = await fetch(`${this.urlValue}&offset=${offset}`, { headers: { Accept: "text/html" } });
      if (!response.ok) throw new Error(`Failed to load page: ${response.status}`);

      const template = document.createElement("template");
      template.innerHTML = (await response.text()).trim();

      template.content.querySelectorAll("[data-group-key]").forEach((heading) => {
        if (this.listTarget.querySelector(`[data-group-key="${heading.dataset.groupKey}"]`)) heading.remove();
      });

      // Counted before de-duping: a short page from the server means the end of the list.
      const rows = template.content.querySelectorAll(".SchoolTask");
      const rowCount = rows.length;
      rows.forEach((row) => {
        if (row.id && document.getElementById(row.id)) row.remove();
      });
      this.listTarget.appendChild(template.content);

      if (rowCount < this.perPageValue) this.stop();
    } catch (error) {
      console.error("Error loading more tasks:", error);
    } finally {
      this.isLoading = false;
      if (this.hasLoadingTarget) this.loadingTarget.style.display = "none";
    }
  }

  stop() {
    this.hasMore = false;
    if (this.observer) this.observer.disconnect();
    if (this.hasTriggerTarget) this.triggerTarget.remove();
  }
}
